/**
 * palette.js — the team's official colours, and the role → colour map per report.
 *
 * Source of truth for the hex values is the team's own palette
 * (~/.claude/projects/…/memory/osteo3d-color-codes.md, from the team 2026-06-21).
 * They were CONFIRMED against the shipped reports rather than trusted: sampling
 * the demo PDFs' pixels and comparing hues showed the report renders really are
 * these albedos under matcap shading —
 *
 *   part                 official      measured p90 on the page   hue match
 *   bone / cranium       #CFC1A7       #DED2AE family             yes
 *   maxilla / Le Fort    #8893F7       #7577AB                    yes (~233°)
 *   mandible / BSSO      #91DD6A       #90C861                    yes (~95°)
 *   genioplasty segment  #D684E1       #C875AB                    yes
 *   fibula segments      per-segment   confirmed on FFF p9/p25    yes
 *   resection area       #FF8F8F       confirmed on FFF p4-p8     yes
 *
 * The measured values are DARKER than the albedo because the renders are shaded;
 * do not "correct" the constants to the measured numbers or every model will
 * come out muddy.
 */

/** Bone / illustration tones — also the SSE report's bone-tone presets. */
export const BONE_TONES = [
  ['Bone', '#CFC1A7'],
  ['Beige', '#ECD8C6'],
  ['Bone Z', '#F5EBCB'],
]

export const BONE = '#CFC1A7'

/** Every named colour the team uses, so the picker offers real choices only. */
export const OFFICIAL_COLORS = {
  bone: '#CFC1A7',
  beige: '#ECD8C6',
  boneZ: '#F5EBCB',
  lightBlue: '#52BEED',
  oliveGreen: '#A1B579',
  oliveGreenZbr: '#555B46',
  babyPink: '#F4C2C2',
  red: '#CC4343',
  bambuGreen: '#91DD6A',

  // planning / case-report anatomy coding
  resection: '#FF8F8F',
  inferiorAlveolarNerve: '#97F6F6',
  bloodVessels: '#FF7276',
  skinLateralMalleolus: '#AE9076',
  mockImplant: '#B2EE91',

  // orthognathic
  osteotomy: '#8893F7',        // Le Fort I / BSSO cut segment — the report's "maxilla blue"
  maxillaMovement: '#88C9FC',
  mandibleOsteotomy: '#91DD6A',
  genioplasty: '#D684E1',
  trimming: '#F95757',

  // Fabrication outputs print in a neutral resin tone.
  //
  // WHITE, not the old #E8E8EA. Under the matcap a colour MULTIPLIES the
  // lit-sphere, so any value below white can only pull the part further from the
  // white the team asked for — and #E8E8EA was doing exactly that on top of a
  // matcap that is already cream. These roles are rendered against the NEUTRAL
  // matcap (see `neutralNonAnatomy` in meshRenderer.js); white here means "take
  // that lit-sphere as authored", which is the same convention anatomy uses.
  splint: '#FFFFFF',
  guide: '#FFFFFF',
}

/**
 * Roles that are FABRICATED, not anatomy — splints, guides, trays, and the
 * intraoral scans and teeth that are shown as appliances rather than bone.
 *
 * The team's report: these should read WHITE and did not. They could not. The
 * bone matcap bakes a cream lit-sphere, MeshMatcapMaterial multiplies by it, and
 * so nothing rendered through it can be brighter or less warm than the matcap
 * itself — measured face-on at #D3C8A7. The polypaint on these meshes is already
 * pure white and it still came out cream, which is the giveaway that no colour
 * change could ever have fixed it.
 *
 * So the parts carrying these roles are rendered against a NEUTRAL lit-sphere
 * derived from the bone one, and this set is what decides which parts those are.
 */
export const NON_ANATOMY_ROLES = new Set([
  'splint', 'splint_intermediate', 'splint_final', 'splint_movement',
  'guide', 'mandible_guide', 'fibula_guide', 'alignment_tray',
  'ios', 'ios_upper', 'ios_lower', 'ios_buccal', 'ios_bite',
  'teeth', 'upper_teeth', 'lower_teeth',
  // ── orbital ──
  // The orbital volumes are MEASUREMENT SOLIDS, not bone, and they must read as
  // the cool pink / purple / green of the reference. Measured: rendered through
  // the bone matcap our pink albedo #E289A6 came out #8D4E48 — brick, not pink —
  // against the reference's #AD6979, because the bone lit-sphere is warm cream
  // (face-on #D3C8A7) and a multiply through it cannot be cooler than it is.
  // Exactly the defect this set was created for; the volumes belong in it.
  'orbital_volume_left', 'orbital_volume_right',
  // The difference model and the trial-fitment appliance are fabricated too.
  'orbital_difference',
])

/** Is this role a fabricated appliance rather than anatomy? Never throws. */
export const isNonAnatomy = (role) => NON_ANATOMY_ROLES.has(role)

/**
 * Fibula segment colours. Left and Right differ ONLY in that Seg01 and Seg03
 * swap (orange ↔ blue) — that is the team's convention, not a typo.
 */
export const FIBULA_SEGMENTS = {
  left: ['#F6AB7E', '#BAF67E', '#7988E0', '#FC9AF7', '#F7FA48'],
  right: ['#7988E0', '#BAF67E', '#F6AB7E', '#FC9AF7', '#F7FA48'],
}

export const fibulaSegmentColor = (side, index) =>
  (FIBULA_SEGMENTS[side] ?? FIBULA_SEGMENTS.left)[index] ?? BONE

/**
 * Role → colour, per template.
 *
 * A role missing from the map falls back to bone, which is the right default:
 * an unclassified mesh should look like the anatomy it probably is, not pick up
 * a colour that implies a meaning nobody assigned. The operator opts in to
 * colour — the design portal already learned this the hard way, when colouring
 * OBJ groups by file order made group 1 look uncoloured and a sixth group wrap
 * back to bone.
 */
const ORTHOGNATHIC_ROLES = {
  skull: BONE,
  cranium: BONE,
  maxilla: OFFICIAL_COLORS.osteotomy,
  maxilla_teeth: OFFICIAL_COLORS.osteotomy,
  mandible: OFFICIAL_COLORS.mandibleOsteotomy,
  mandible_teeth: OFFICIAL_COLORS.mandibleOsteotomy,
  genio_segment: OFFICIAL_COLORS.genioplasty,
  mandibular_canal: OFFICIAL_COLORS.inferiorAlveolarNerve,
  upper_teeth: OFFICIAL_COLORS.splint,
  lower_teeth: OFFICIAL_COLORS.splint,
  teeth: OFFICIAL_COLORS.splint,
  splint: OFFICIAL_COLORS.splint,
  splint_intermediate: OFFICIAL_COLORS.splint,
  splint_final: OFFICIAL_COLORS.splint,
  splint_movement: OFFICIAL_COLORS.splint,
  guide: OFFICIAL_COLORS.guide,
  // The two specific guide roles, here as well as in FIBULA_ROLES, because
  // `classifyRole` now emits them for ANY template. An orthognathic case naming
  // a mandibular guide would otherwise fall through to bone — a white printed
  // appliance rendered as anatomy. FIBULA_ROLES and ORBITAL_ROLES spread this
  // map, so both inherit the fix.
  mandible_guide: OFFICIAL_COLORS.guide,
  fibula_guide: OFFICIAL_COLORS.guide,
  ios: OFFICIAL_COLORS.splint,
  ios_upper: OFFICIAL_COLORS.splint,
  ios_lower: OFFICIAL_COLORS.splint,
  ios_buccal: OFFICIAL_COLORS.splint,
  ios_bite: OFFICIAL_COLORS.splint,
  other: BONE,
}

/**
 * The fibula report's own coding. Segment roles are not produced by classify.js
 * yet (its filename vocabulary is still unknown — see FIBULA_RULES_PENDING), so
 * these are reachable today only when the operator sets a role by hand. The
 * colours are recorded now so that when the corpus arrives, only the classifier
 * needs writing.
 */
const FIBULA_ROLES = {
  ...ORTHOGNATHIC_ROLES,
  mandible: BONE,
  maxilla: BONE,
  fibula: BONE,
  tibia: BONE,
  leg: BONE,
  resection: OFFICIAL_COLORS.resection,
  blood_vessels: OFFICIAL_COLORS.bloodVessels,
  skin: OFFICIAL_COLORS.skinLateralMalleolus,
  reconstructed_mandible: BONE,
  alignment_tray: OFFICIAL_COLORS.guide,
  mandible_guide: OFFICIAL_COLORS.guide,
  fibula_guide: OFFICIAL_COLORS.guide,
  // The role the CLASSIFIER actually emits for `Fibula Segments.OBJ` — one role
  // for the whole multi-segment mesh. Without it the file fell through to bone,
  // i.e. the same colour as the leg it sits inside, and the segments were
  // invisible on the delivered report's page 9.
  //
  // One colour for the whole mesh until the operator declares its polygroups.
  // Deliberately the first segment's colour rather than a new one: inventing a
  // colour outside the team's palette would be worse than reusing theirs. It is
  // NOT side-correct — `colorForRole` has no mesh and so no side — which is
  // precisely why the per-segment declaration exists, and why the recipe raises
  // a note when the groups have not been declared.
  //
  // ⚠️ NOT in STAGE_CODED_ROLES, and that is not an exception to that set's rule
  // but its rule applied: colour there asserts "the plan has moved this part",
  // which is why a native mandible stays bone. A file called *Fibula Segments*
  // only exists because the plan cut it — the segmentation IS the planning act,
  // so colouring it asserts nothing that has not happened.
  fibula_segments: FIBULA_SEGMENTS.left[0],
  fibula_segment_1: FIBULA_SEGMENTS.left[0],
  fibula_segment_2: FIBULA_SEGMENTS.left[1],
  fibula_segment_3: FIBULA_SEGMENTS.left[2],
  fibula_segment_4: FIBULA_SEGMENTS.left[3],
  fibula_segment_5: FIBULA_SEGMENTS.left[4],
}

/**
 * Orbital volume coding, for the "Orbital volume calculation" page and its legend.
 *
 * `onPage` was MEASURED on the delivered reference — "Images for Design
 * Confirmation - O3D-2026-06-18-0001_a.pdf" (V1.3.3, page 3) — at 600 dpi, as the
 * interior median of each 2.8 mm swatch. See
 * docs/case-report/ORBITAL-REFERENCE-MEASUREMENTS.md §6.
 *
 * ⚠️ `onPage` is NOT a fill value. Those samples come off a matcap-shaded render,
 * through a baked-in watermark, in a 200 dpi raster — every one of which darkens
 * what it passes. `albedo` is the fill that should land on `onPage` after
 * shading, and it is currently DERIVED, not confirmed: the design team has not
 * yet been asked what values they use in CAD. When they answer, their values
 * supersede these and the fit is re-checked against `onPage`.
 *
 * The same caution the file header gives for bone applies here, for the same
 * reason.
 */
export const ORBITAL_VOLUME_COLORS = {
  nativeRightOrbit: { onPage: '#C27F90', albedo: '#E289A6' },   // pink
  nativeLeftOrbit: { onPage: '#887092', albedo: '#9B72B8' },    // purple
  postPlanOrbit: { onPage: '#A4BA4E', albedo: '#A9C93F' },      // green
}

/**
 * Orbital roles.
 *
 * Note what green means here: it is the POST-PLAN orbit, whichever side that is.
 * So the colour codes the planning stage, not the anatomy — the same principle
 * STAGE_CODED_ROLES applies to the orthognathic segments, and the reason
 * `colorForRole` takes a stage at all.
 *
 * The difference model and the trial fitment model are fabrication outputs and
 * take the neutral resin tone, like a splint or a guide.
 */
const ORBITAL_ROLES = {
  ...ORTHOGNATHIC_ROLES,
  orbital_volume_right: ORBITAL_VOLUME_COLORS.nativeRightOrbit.albedo,
  orbital_volume_left: ORBITAL_VOLUME_COLORS.nativeLeftOrbit.albedo,
  orbital_difference: OFFICIAL_COLORS.splint,   // the white fabrication tone
  trial_fitment_model: BONE,
}

export const ROLE_COLORS = {
  orthognathic: ORTHOGNATHIC_ROLES,
  fibula: FIBULA_ROLES,
  orbital: ORBITAL_ROLES,
}

/**
 * Roles whose colour is decided by the planning stage rather than the role.
 *
 * An orbital volume is pink or purple when it is NATIVE (by side) and green when
 * it is the POST-PLAN orbit. `colorForRole` cannot express "green if post-plan"
 * through a flat lookup, so it is resolved here.
 */
const ORBITAL_STAGE_COLORS = {
  orbital_volume_left: {
    native: ORBITAL_VOLUME_COLORS.nativeLeftOrbit.albedo,
    postplan: ORBITAL_VOLUME_COLORS.postPlanOrbit.albedo,
  },
  orbital_volume_right: {
    native: ORBITAL_VOLUME_COLORS.nativeRightOrbit.albedo,
    postplan: ORBITAL_VOLUME_COLORS.postPlanOrbit.albedo,
  },
}

/**
 * Anatomy roles whose colour is CONDITIONAL on the planning stage.
 *
 * Read off the reference report, page by page, and the pattern is exact:
 *
 *   p3  Native Skull ................ all cream
 *   p8  Native | Post Plan .......... native column cream; post-plan column
 *                                     blue maxilla, green mandible, magenta genio
 *   p10 Intermediate Splint ......... maxilla BLUE, mandible still CREAM
 *   p12 Final Splint ................ maxilla blue AND mandible green
 *
 * At the intermediate splint the maxilla has been repositioned and the mandible
 * has not — and that is exactly which one is coloured. So the colour code does
 * not mean "this is a mandible". It means **the plan has moved this segment.**
 *
 * Colouring native anatomy would therefore assert a movement that has not
 * happened, on a document a surgeon reads. Native stays bone.
 */
const STAGE_CODED_ROLES = new Set([
  'maxilla', 'maxilla_teeth', 'mandible', 'mandible_teeth', 'genio_segment',
  'fibula_segment_1', 'fibula_segment_2', 'fibula_segment_3', 'fibula_segment_4',
  'fibula_segment_5', 'resection',
])

/**
 * Default colour for a classified mesh.
 *
 * @param {string} template  'orthognathic' | 'fibula' | 'orbital'
 * @param {string} role      from classify.js
 * @param {string} [stage]   from classify.js. Anatomy is bone until the plan has
 *                           moved it; omit only when the stage is genuinely
 *                           unknown, which is treated as native.
 *
 * Never throws and never returns undefined — an unknown role gets bone.
 */
export function colorForRole(template, role, stage = 'native') {
  // Orbital volumes are coded by side when native and green when post-plan, so a
  // flat role lookup cannot answer. Checked before STAGE_CODED_ROLES because
  // these roles are NOT bone when native — they are the measured solids the page
  // exists to compare, and drawing them bone would hide both of them inside the
  // skull.
  const byStage = ORBITAL_STAGE_COLORS[role]
  if (byStage) return byStage[stage] ?? byStage.native
  if (STAGE_CODED_ROLES.has(role) && stage === 'native') return BONE
  // A format with no colour set of its own paints bone, never another
  // template's colours: silently borrowing the orthognathic map would have
  // rendered a resection red as bone and a fibula segment as a maxilla.
  return (ROLE_COLORS[template] ?? {})[role] ?? BONE
}

/** #rrggbb → [r,g,b]. */
export function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex ?? ''))
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/**
 * The deviation ramp for the interference page: a hue sweep from red through
 * green to blue, matching the legend on the shipped report — 0.05 mm red,
 * 0.125 mm green, 0.2 mm blue.
 *
 * Note the direction: LOW distance is RED. On that page red marks where two
 * segments are within 0.05 mm of each other, i.e. the collisions. It is the
 * opposite of a textbook jet ramp, and getting it backwards would colour the
 * interference-free regions as the problem.
 *
 * A true jet was tried first and rejected: it bottoms out at half-intensity
 * dark blue and dark red, where the report's legend is fully saturated at both
 * ends.
 */
export function deviationColor(t) {
  const hue = Math.min(1, Math.max(0, t)) * 240 // 0° red → 120° green → 240° blue
  const c = hue / 60
  const x = 1 - Math.abs((c % 2) - 1)
  const [r, g, b] =
    c < 1 ? [1, x, 0]
      : c < 2 ? [x, 1, 0]
        : c < 3 ? [0, 1, x]
          : [0, x, 1]
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
}

/**
 * The appearances an operator can declare for a mesh, or for one polygroup
 * inside it. Deliberately APPEARANCES and not clinical roles: `mandible`
 * colours by planning stage, so a role dropdown would paint a trial-fitment
 * base osteotomy-green — a surprise no operator can predict from the label.
 *
 * Single source for both the UI and partAppearance, so the two cannot drift.
 */
export const PART_PRESETS = [
  { id: '', label: 'Auto — as detected', spec: null },
  { id: 'guide', label: 'Guide (white resin)', spec: 'guide' },
  { id: 'splint', label: 'Splint (white resin)', spec: 'splint' },
  { id: 'anatomy', label: 'Anatomy (bone)', spec: { color: BONE, neutral: false } },
  // Team, 10 Oct 2026: "The colour assigned to the Titanium PSI implant is too dark. Please
  // change it to a slightly lighter shade of grey." Both halves of that were wrong, and the
  // second only showed up on a render:
  //
  //   · `neutral: false` put a fabricated part on the BONE lit-sphere, so a grey tint printed
  //     KHAKI, not grey. Measured on the team's own implant, O3D-2026-08-20-0001: #4A4A4A came
  //     out rgb(52,48,37), R-B spread 15. There was no grey to lighten.
  //   · the colour is a TINT multiplied into the matcap, not a surface colour, so 74/255 cut the
  //     lit-sphere to under a third.
  //
  // On the neutral sphere the ink is exactly neutral (R=G=B) and the same tint already reads
  // lighter. Measured, this pair: rgb(115,115,115), lightness 115 against the old 48. Deepak
  // picked it off the rendered sheet on 10 Oct rather than off the number, which is the only way
  // a matcap tint can be chosen.
  { id: 'insert', label: 'Metal insert (dark)', spec: { color: '#6E6E6E', neutral: true } },
  // The five harvested fibula segments. Declared by the OPERATOR, per polygroup
  // — Deepak, 2026-09-15: the segment order inside the OBJ is the exporter's,
  // not a stated intent, so it is not derived from it.
  //
  // 🔴 These carry `{segment: n}`, NOT a role name, and the reason is the stage
  // gate. `fibula_segment_1..5` are in STAGE_CODED_ROLES, which returns BONE for
  // them at `stage === 'native'` — and the real `Fibula Segments.OBJ` classifies
  // native, because its filename names no stage. So a preset declaring the ROLE
  // would render bone and the operator's declaration would appear not to work.
  // `anatomy` and `insert` above set the precedent of spelling the appearance
  // out instead.
  //
  // The colour is resolved in `partAppearance`, which has the mesh and therefore
  // its SIDE — the team's convention swaps segments 1 and 3 between left and
  // right, so a static colour here would be wrong for half of all cases.
  ...[1, 2, 3, 4, 5].map((n) => ({
    id: `fibula_segment_${n}`,
    label: `Fibula segment ${n}`,
    spec: { segment: n },
  })),
]

/**
 * A declaration of "this is harvested fibula segment n", resolved against the
 * mesh's own side.
 *
 * Told apart from an appearance and from a group map by carrying `segment`.
 * Order matters in `partAppearance`: without this test `{segment: 1}` is read as
 * a group map with a group called "segment", which is the same defect the
 * `isAppearance` comment below records having shipped once already.
 */
const isSegment = (v) => v && typeof v === 'object' && 'segment' in v

/** Segment n's colour for this mesh's side. `fibulaSegmentColor` handles a null side. */
const segmentAppearance = (mesh, spec) => ({
  color: fibulaSegmentColor(mesh?.side, spec.segment - 1),
  neutral: false,
})

/** id → the spec `partAppearance` consumes. Unknown ids resolve to null (auto). */
export const specForPreset = (id) => PART_PRESETS.find((p) => p.id === id)?.spec ?? null

/**
 * Resolve a mesh's colour and neutral flag, honouring an operator declaration.
 * `declared` is absent (auto), a role name, an appearance, or a map of
 * POLYGROUP name → one of those.
 */
/**
 * A declaration arrives from the UI as a PRESET ID — that is what the <select>
 * carries. Two of the five presets ('anatomy', 'insert') are not roles at all,
 * so treating an id as a role silently mis-resolved them: `insert` asked for a
 * dark metal #6E6E6E and got bone, because colorForRole falls back to bone for
 * anything it does not recognise. Caught by measuring a render, not by a test.
 *
 * Unknown strings still fall through as roles, so a stored role name from an
 * older declaration keeps working.
 */
const asSpec = (d) => (typeof d === 'string' ? (specForPreset(d) ?? d) : d)

export function partAppearance(template, mesh, declaredRaw) {
  const declared = typeof declaredRaw === 'string'
    ? asSpec(declaredRaw)
    : declaredRaw
  const base = {
    color: colorForRole(template, mesh.role, mesh.stage),
    neutral: isNonAnatomy(mesh.role),
  }
  if (!declared) return base

  // Whole-mesh declaration: a role name, or an appearance spelled out. An
  // appearance is told apart from a GROUP MAP by carrying `color` or `neutral` —
  // without that test `{color:'#CFC1A7'}` is read as a map with a group called
  // "color", which is exactly what it did, silently, on the first run.
  const isAppearance = (v) => v && typeof v === 'object' && ('color' in v || 'neutral' in v)
  // Before the group-map fallthrough, for the reason `isSegment` records.
  if (isSegment(declared)) return segmentAppearance(mesh, declared)
  if (typeof declared === 'string' || isAppearance(declared)) {
    const spec = typeof declared === 'string' ? { role: declared } : declared
    return {
      color: spec.color ?? colorForRole(template, spec.role, mesh.stage),
      neutral: spec.neutral ?? isNonAnatomy(spec.role),
    }
  }
  const groups = {}
  const neutralGroups = {}
  for (const [name, raw] of Object.entries(declared)) {
    const spec = asSpec(raw)
    // A group may be declared as a ROLE ('guide') or spelled out
    // ({ color, neutral }). The explicit form exists because the delivered
    // reference needs appearances no role carries: the dark screw inserts in a
    // fixation guide, and a trial-fitment model that must read as bone even
    // though its role would colour it as an osteotomy segment. Inventing roles
    // for those would put presentation into the clinical vocabulary.
    // A group declared as a fibula segment, same as the whole-mesh case above.
    // This is the path the operator actually uses: one OBJ holds all five
    // segments as polygroups, and they declare them one by one.
    if (isSegment(spec)) {
      const seg = segmentAppearance(mesh, spec)
      groups[name] = seg.color
      neutralGroups[name] = seg.neutral
      continue
    }
    const asRole = typeof spec === 'string' ? spec : spec?.role
    groups[name] = spec?.color ?? colorForRole(template, asRole, mesh.stage)
    neutralGroups[name] = spec?.neutral ?? isNonAnatomy(asRole)
  }
  return { ...base, groups, neutralGroups }
}
